// s101 (Dr. Rami, Build 67 feedback, 2026-10-13): "Specialty is locked but also should be open to
// PCP (our problems allow sending to different types of doctors depending on the selection - set in
// admin panel)" and "Always allow PCP selection".
// ★★★ ONE OWNER for the picker's specialty choices. They come from the case area's
//   `matchingDoctorSpecialties` in the LIVE catalog (edited in the admin panel), and PRIMARY CARE
//   IS ALWAYS ONE OF THEM. The same choice narrows BOTH lists - the Orella doctors (by specialty
//   name) and the national directory (by NPPES taxonomy prefix) - so the two never disagree.
// Pure (no imports): the gate check-specialty-choices runs it.

// PROD-1183 (Alex 2026-10-19): adult "Primary care" = general-care subtypes only. Family Medicine
//   (every 207Q code), general and geriatric Internal Medicine, General Practice, NP generic / Family /
//   Primary Care / Adult Health / Gerontology, PA and PA Medical. A 10-character entry is a full NUCC
//   code, so the directory matches it exactly; a bare '363L' / '363A' would pull in every NP / PA
//   subtype (psych, women's health, acute care ...). Used by the share picker, the Find Doctor chip,
//   the directory banner and the Doctors tab.
export const PRIMARY_CARE_TAXONOMY = ['207Q', '207R00000X', '207RG0300X', '208D00000X', '363L00000X', '363LF0000X', '363LP2300X', '363LA2200X', '363LG0600X', '363A00000X', '363AM0700X']

// PROD-1183: the patient's OWN primary-care finder (components/PrimaryCareDoctor.jsx) stays BROAD on
//   purpose. There the patient looks up a clinician they already know by name, so every NP and PA
//   subtype must stay findable (e.g. a women's-health NP who is their regular clinician). Do not
//   replace this with PRIMARY_CARE_TAXONOMY; check-pcp-profile holds it.
export const PCP_FINDER_TAXONOMY = ['207Q', '207R00000X', '363L', '363A']

const FAMILY = new Set(['general medicine', 'family medicine', 'internal medicine', 'general practice', 'family practice', 'primary care'])
export const isPrimaryCareName = (s) => FAMILY.has(String(s || '').toLowerCase().trim())

// The catalog's doctor-specialty names -> NPPES taxonomy codes (provider search `taxonomy=`).
// ★ PROD-1188: the bare prefixes that pulled in the wrong NUCC codes are sent as the exact codes the API now
//   uses (api _PROVIDER_SEARCH_PREFIX_FIX; scripts/check-specialty-choices.mjs holds the two tables equal):
//   Gastroenterology 207RG -> 207RG0100X (the bare prefix held geriatric internists 207RG0300X); Cardiology
//   207RC -> five cardiology codes (held intensivists 207RC0200X); Infectious Disease 207RI -> 207RI0200X;
//   Psychiatry 2084P -> five psychiatry codes (held pain medicine 2084P2900X); Dermatology 207N -> five codes
//   (held dermatopathology 207ND0900X); Dental 122 -> 1223 (held denturists 122400000X).
//   Audiology (Alex 2026-10-21, answer a) -> 231H: the API lists Otolaryngology (ENT) doctors for it; without an
//   entry the filter sent no taxonomy and showed every doctor. ★ SHIP ORDER: the API (which knows 231H) first,
//   then this app; an older API finds no clinician for 231H.
// ★ PROD-1316: Endocrinology, Rheumatology, Nephrology, Pulmonology, Pain Management, Sleep Medicine, Sports Medicine
//   and Oncology send exactly the codes of the API's _REFERRAL_TAXONOMY_PREFIXES (the server's referral table);
//   scripts/check-specialty-choices.mjs (R1) holds them equal. Sports 207XX0801X -> 207XX0005X as on the server.
export const SPECIALTY_TAXONOMY = {
  'Otolaryngology (ENT)': ['207Y'], 'Ophthalmology': ['207W'], 'Allergy & Immunology': ['207K'],
  'Endocrinology': ['207RE0101X'], 'Orthopedics': ['207X'], 'Physical Medicine & Rehab': ['2081'],
  'Rheumatology': ['207RR0500X'], 'Sports Medicine': ['207QS0010X', '207XX0005X', '2081S0010X', '207RS0010X'],
  'Pain Management': ['208VP', '207LP2900X', '2081P2900X', '2084P2900X'],
  'Oncology': ['207RX0202X', '207RH0003X', '2086X0206X', '2085R0001X'], 'Dermatology': ['207N00000X', '207ND0099X', '207NI0002X', '207NP0225X', '207NS0135X'], 'Gastroenterology': ['207RG0100X'],
  'Urology': ['2088'], 'Cardiology': ['207RC0000X', '207RC0001X', '207RI0011X', '207RA0001X', '207RA0002X'], 'Pulmonology': ['207RP1001X'], 'Nephrology': ['207RN0300X'],
  'Hematology': ['207RH'], 'Obstetrics & Gynecology': ['207V'], 'Neurology': ['2084N'], 'Psychiatry': ['2084P0800X', '2084P0802X', '2084P0804X', '2084P0805X', '2084P0015X'],
  'Vascular Surgery': ['2086S0129X'], 'Infectious Disease': ['207RI0200X'], 'Sleep Medicine': ['207RS0012X', '2084S0012X', '207QS1201X', '207YS0012X', '2083S0010X'],
  'Podiatry': ['213E'], 'Dental': ['1223'], 'Audiology': ['231H'], 'Plastic Surgery': ['2082'], 'Anesthesiology': ['207L'],
}

export const PRIMARY_CHOICE_ID = 'primary'

/**
 * The picker's specialty choices for a case: each specialty the admin assigned to the case's area
 * (in the catalog's order), then Primary care - always present, exactly once. `areaTaxonomy` is the
 * fallback for a name the table above does not know (a specialty added in admin later).
 * → [{ id, label, targets: [catalog names], taxonomy: [prefixes], primary: bool }]
 */
export function specialtyChoicesFor(matchingSpecialties, areaTaxonomy = []) {
  const out = []
  const seen = new Set()
  for (const name of Array.isArray(matchingSpecialties) ? matchingSpecialties : []) {
    const n = String(name || '').trim()
    if (!n || isPrimaryCareName(n) || seen.has(n)) continue
    seen.add(n)
    out.push({ id: n, label: n, targets: [n], taxonomy: SPECIALTY_TAXONOMY[n] || areaTaxonomy || [], primary: false })
  }
  out.push({ id: PRIMARY_CHOICE_ID, label: 'Primary care', targets: ['General Medicine'], taxonomy: PRIMARY_CARE_TAXONOMY, primary: true })
  return out
}

/** The area's matchingDoctorSpecialties from a catalog (live first), else null. */
export function catalogMatchingSpecialties(specialties, specialtyId) {
  const s = (Array.isArray(specialties) ? specialties : []).find((x) => x && x.id === specialtyId)
  return s && Array.isArray(s.matchingDoctorSpecialties) && s.matchingDoctorSpecialties.length ? s.matchingDoctorSpecialties : null
}

/** Does a doctor fit a choice? `matches(docSpec, target)` is doctorMatch.specialtyMatchesTarget. */
export function doctorFitsChoice(doc, choice, matches) {
  if (!choice) return true
  const specs = Array.isArray(doc && doc.specialties) ? doc.specialties : []
  return specs.some((ds) => choice.targets.some((tg) => matches(ds, tg)))
}

/** Who to show: 'all' | 'orella' | 'other' (the "On Orella" filter). */
export const SHOW_OPTIONS = ['all', 'orella', 'other']
